import { useCallback, useEffect, useState } from "react";

/**
 * A router, in thirty lines.
 *
 * Real paths via the History API, not a hash. `/proposals` is a URL someone can
 * paste into Slack; `/#/proposals` looks like a bug and breaks anything that
 * strips fragments. The server already serves the console shell for any
 * non-`/api` path, so a hard refresh or a deep link lands correctly.
 *
 * No router library: this console has seven flat routes and no nested layouts,
 * params or loaders. A dependency would be more code to read, not less.
 */
export const ROUTES = [
  "dashboard",
  "proposals",
  "chats",
  "keys",
  "webhooks",
  "users",
  "settings",
] as const;

export type Route = (typeof ROUTES)[number];

export function pathToRoute(pathname: string): Route {
  const segment = pathname.replace(/^\/+|\/+$/g, "").split("/")[0] ?? "";
  return (ROUTES as readonly string[]).includes(segment) ? (segment as Route) : "dashboard";
}

export function useRouter(): { route: Route; navigate: (to: Route) => void } {
  const [route, setRoute] = useState<Route>(() => pathToRoute(window.location.pathname));

  // Back and forward have to work, or the browser's own buttons lie.
  useEffect(() => {
    const onPop = () => setRoute(pathToRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: Route) => {
    if (pathToRoute(window.location.pathname) !== to) {
      window.history.pushState({}, "", `/${to}`);
    }
    setRoute(to);
  }, []);

  return { route, navigate };
}

/**
 * Intercept a link click so it navigates in-place.
 *
 * The anchor keeps a real `href`, so middle-click, ctrl-click and "copy link
 * address" all still do the right thing — which is exactly what a `<div
 * onClick>` masquerading as a link takes away.
 */
export function linkProps(to: Route, navigate: (to: Route) => void) {
  return {
    href: `/${to}`,
    onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      navigate(to);
    },
  };
}
